import { useState, useCallback, useEffect } from 'react';
import { SlideOver } from '../../../components/ui/SlideOver';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { FormLabel } from '../../../components/ui/FormLabel';
import { ramsAI } from '../../../api';
import { Sparkles, AlertCircle, ChevronUp, Check, X } from 'lucide-react';

const OPERATIONS = [
  { id: 'build_hazards', label: 'Build hazards' },
  { id: 'build_method_steps', label: 'Build method steps' },
  { id: 'improve_controls', label: 'Improve controls' },
  { id: 'quality_check', label: 'Quality check' },
];

const AI_DISCLAIMER = 'AI output is draft/suggestions only and does not replace duty holder or competent person responsibility.';

function mapHazardFromApi(h, sortOrder) {
  const cm = (h.control_measures || []).map((c) =>
    typeof c === 'object' && c?.text != null ? { text: c.text } : { text: String(c) }
  );
  return {
    id: crypto.randomUUID?.() ?? `h-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    hazardCategory: h.hazard_category ?? h.hazardCategory ?? '',
    hazardText: h.hazard_text ?? h.hazardText ?? '',
    personsAtRisk: Array.isArray(h.persons_at_risk) ? h.persons_at_risk : (h.personsAtRisk || []),
    initialLikelihood: h.initial_likelihood ?? h.initialLikelihood ?? null,
    initialSeverity: h.initial_severity ?? h.initialSeverity ?? null,
    initialRisk: h.initial_risk ?? h.initialRisk ?? null,
    residualLikelihood: h.residual_likelihood ?? h.residualLikelihood ?? null,
    residualSeverity: h.residual_severity ?? h.residualSeverity ?? null,
    residualRisk: h.residual_risk ?? h.residualRisk ?? null,
    controlMeasures: cm,
    sortOrder,
  };
}

function mapMethodStepFromApi(s, order) {
  return {
    id: crypto.randomUUID?.() ?? `s-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    stepOrder: order,
    stepTitle: s.step_title ?? s.stepTitle ?? '',
    stepDetail: Array.isArray(s.step_detail) ? s.step_detail : (s.stepDetail || []),
    linkedHazardCategories: Array.isArray(s.linked_hazard_categories)
      ? s.linked_hazard_categories
      : (s.linkedHazardCategories || []),
  };
}

export default function RamsAISlideOver({
  open,
  onOpenChange,
  form,
  ramsId,
  initialOperation = 'build_hazards',
  improveControlsHazardIndex = null,
  onAcceptHazards,
  onAcceptMethodSteps,
  onAcceptImproveControls,
  onAcceptPpeToolsPermits,
}) {
  const [operation, setOperation] = useState(initialOperation);
  const [taskSummary, setTaskSummary] = useState('');
  const [contextSiteName, setContextSiteName] = useState('');
  const [contextWorkType, setContextWorkType] = useState('');
  const [contextOther, setContextOther] = useState('');
  const [deeper, setDeeper] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [acceptedBlocks, setAcceptedBlocks] = useState({ hazards: [], methodSteps: [], improve: false, ppeToolsPermits: false });

  useEffect(() => {
    if (open) {
      setOperation(initialOperation);
    }
  }, [open, initialOperation]);

  const resetResult = useCallback(() => {
    setResult(null);
    setError('');
    setAcceptedBlocks({ hazards: [], methodSteps: [], improve: false, ppeToolsPermits: false });
  }, []);

  const runRequest = useCallback(
    async (escalate = false) => {
      setError('');
      setLoading(true);
      setResult(null);
      const payload = { ramsId: ramsId || undefined, escalate };
      try {
        if (operation === 'build_hazards') {
          const res = await ramsAI.buildHazards({
            ...payload,
            taskSummary: taskSummary.trim(),
            context: {
              siteName: contextSiteName.trim() || undefined,
              workType: contextWorkType.trim() || undefined,
              otherContext: contextOther.trim() || undefined,
            },
          });
          setResult(res);
          setConsecutiveFailures(0);
        } else if (operation === 'build_method_steps') {
          const hazards = (form?.hazards || []).map((h) => ({
            hazard_category: h.hazardCategory,
            hazard_text: h.hazardText,
            persons_at_risk: h.personsAtRisk,
          }));
          const res = await ramsAI.buildMethodSteps({
            ...payload,
            taskSummary: taskSummary.trim(),
            hazards,
          });
          setResult(res);
          setConsecutiveFailures(0);
        } else if (operation === 'improve_controls') {
          const hazard = form?.hazards?.[improveControlsHazardIndex ?? 0];
          if (!hazard) {
            setError('Select a hazard to improve in the editor first.');
            setLoading(false);
            return;
          }
          const hazardRow = {
            hazard_category: hazard.hazardCategory,
            hazard_text: hazard.hazardText,
            persons_at_risk: hazard.personsAtRisk,
            control_measures: (hazard.controlMeasures || []).map((c) =>
              typeof c === 'object' && c?.text != null ? c : { text: String(c) }
            ),
          };
          const res = await ramsAI.improveControls({ ...payload, hazardRow });
          setResult(res);
          setConsecutiveFailures(0);
        } else if (operation === 'quality_check') {
          const ramsContent = form
            ? {
                title: form.title,
                description: form.description,
                hazards: form.hazards,
                methodSteps: form.methodSteps,
                ppe: form.ppe,
                tools: form.tools,
                permits: form.permits,
                emergencyArrangements: form.emergencyArrangements,
              }
            : {};
          const res = await ramsAI.qualityCheck({
            ...payload,
            ramsContent,
            deeper,
          });
          setResult(res);
          setConsecutiveFailures(0);
        }
      } catch (err) {
        const msg = err.response?.data?.error || err.message || 'AI request failed';
        setError(msg);
        setConsecutiveFailures((c) => c + 1);
        setResult(null);
      } finally {
        setLoading(false);
      }
    },
    [
      operation,
      taskSummary,
      contextSiteName,
      contextWorkType,
      contextOther,
      deeper,
      form,
      improveControlsHazardIndex,
      ramsId,
    ]
  );

  const handleRetry = () => {
    runRequest(consecutiveFailures >= 2);
  };

  const handleEscalate = () => {
    runRequest(true);
  };

  const handleAcceptAllHazards = () => {
    if (!result?.data?.hazards?.length || !onAcceptHazards) return;
    const hazards = result.data.hazards.map((h, i) => mapHazardFromApi(h, (form?.hazards?.length ?? 0) + i));
    onAcceptHazards(hazards);
    setAcceptedBlocks((b) => ({ ...b, hazards: result.data.hazards.map((_, i) => i) }));
  };

  const handleAcceptHazard = (idx) => {
    if (!result?.data?.hazards?.[idx] || !onAcceptHazards) return;
    const h = mapHazardFromApi(result.data.hazards[idx], (form?.hazards?.length ?? 0));
    onAcceptHazards([h]);
    setAcceptedBlocks((b) => ({ ...b, hazards: [...new Set([...b.hazards, idx])] }));
  };

  const handleRejectHazard = (idx) => {
    setAcceptedBlocks((b) => ({ ...b, hazards: b.hazards.filter((i) => i !== idx) }));
  };

  const handleAcceptAllMethodSteps = () => {
    if (!result?.data?.method_steps?.length || !onAcceptMethodSteps) return;
    const steps = result.data.method_steps.map((s, i) => mapMethodStepFromApi(s, (form?.methodSteps?.length ?? 0) + i));
    onAcceptMethodSteps(steps);
    setAcceptedBlocks((b) => ({ ...b, methodSteps: result.data.method_steps.map((_, i) => i) }));
  };

  const handleAcceptMethodStep = (idx) => {
    if (!result?.data?.method_steps?.[idx] || !onAcceptMethodSteps) return;
    const s = mapMethodStepFromApi(result.data.method_steps[idx], (form?.methodSteps?.length ?? 0));
    onAcceptMethodSteps([s]);
    setAcceptedBlocks((b) => ({ ...b, methodSteps: [...new Set([...b.methodSteps, idx])] }));
  };

  const handleAcceptImprove = () => {
    if (!result?.data || !onAcceptImproveControls || improveControlsHazardIndex == null) return;
    onAcceptImproveControls(improveControlsHazardIndex, result.data);
    setAcceptedBlocks((b) => ({ ...b, improve: true }));
  };

  const handleAcceptPpeToolsPermitsBlock = () => {
    if (!result?.data || !onAcceptPpeToolsPermits) return;
    const payload = {
      ppe: (result.data.ppe || []).map((p, i) => ({
        id: crypto.randomUUID?.() ?? `p-${Date.now()}-${i}`,
        item: typeof p === 'string' ? p : p.item ?? '',
        required: p.required !== false,
        sortOrder: (form?.ppe?.length ?? 0) + i,
      })),
      tools: (result.data.tools || []).map((t, i) => ({
        id: crypto.randomUUID?.() ?? `t-${Date.now()}-${i}`,
        item: typeof t === 'string' ? t : t.item ?? '',
        sortOrder: (form?.tools?.length ?? 0) + i,
      })),
      permits: (result.data.permits || []).map((p, i) => ({
        id: crypto.randomUUID?.() ?? `perm-${Date.now()}-${i}`,
        permitType: typeof p === 'string' ? p : p.permit_type ?? p.permitType ?? '',
        required: p.required !== false,
        notes: typeof p === 'object' ? (p.notes ?? '') : '',
        sortOrder: (form?.permits?.length ?? 0) + i,
      })),
      emergency: result.data.emergency ?? '',
    };
    onAcceptPpeToolsPermits(payload);
    setAcceptedBlocks((b) => ({ ...b, ppeToolsPermits: true }));
  };

  const whenOpenChange = (next) => {
    if (!next) {
      resetResult();
      setConsecutiveFailures(0);
    }
    onOpenChange(next);
  };

  return (
    <SlideOver
      open={open}
      onOpenChange={whenOpenChange}
      title="RAMS AI assist"
      width="w-full max-w-2xl"
    >
      <div className="space-y-4 text-gray-900 dark:text-gray-100">
        <p className="text-xs text-gray-500 dark:text-gray-400">{AI_DISCLAIMER}</p>

        <div>
          <FormLabel className="text-gray-700 dark:text-gray-300">Operation</FormLabel>
          <div className="flex flex-wrap gap-2 mt-1">
            {OPERATIONS.map((op) => (
              <Button
                key={op.id}
                type="button"
                variant={operation === op.id ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setOperation(op.id);
                  resetResult();
                }}
              >
                {op.label}
              </Button>
            ))}
          </div>
        </div>

        {operation === 'build_hazards' && (
          <>
            <div>
              <FormLabel className="text-gray-700 dark:text-gray-300">Task summary *</FormLabel>
              <Textarea
                value={taskSummary}
                onChange={(e) => setTaskSummary(e.target.value)}
                className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[80px]"
                placeholder="Describe the work/task"
              />
            </div>
            <div>
              <FormLabel className="text-gray-700 dark:text-gray-300">Site name (optional)</FormLabel>
              <Input
                value={contextSiteName}
                onChange={(e) => setContextSiteName(e.target.value)}
                className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="Site name"
              />
            </div>
            <div>
              <FormLabel className="text-gray-700 dark:text-gray-300">Work type (optional)</FormLabel>
              <Input
                value={contextWorkType}
                onChange={(e) => setContextWorkType(e.target.value)}
                className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="e.g. Installation, Testing"
              />
            </div>
            <div>
              <FormLabel className="text-gray-700 dark:text-gray-300">Additional context (optional)</FormLabel>
              <Textarea
                value={contextOther}
                onChange={(e) => setContextOther(e.target.value)}
                className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[60px]"
              />
            </div>
          </>
        )}

        {operation === 'build_method_steps' && (
          <div>
            <FormLabel className="text-gray-700 dark:text-gray-300">Task summary *</FormLabel>
            <Textarea
              value={taskSummary}
              onChange={(e) => setTaskSummary(e.target.value)}
              className="border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[80px]"
              placeholder="Describe the work for method steps"
            />
            {form?.hazards?.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Current hazard categories will be sent to link steps.
              </p>
            )}
          </div>
        )}

        {operation === 'improve_controls' && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {improveControlsHazardIndex != null
              ? `Improving controls for hazard ${improveControlsHazardIndex + 1}.`
              : 'Select a hazard in the editor and use "Improve controls" from the hazard row, or run improve for the first hazard.'}
          </p>
        )}

        {operation === 'quality_check' && (
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={deeper}
            onChange={(e) => setDeeper(e.target.checked)}
            className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
          />
          Deeper / more thorough review
          </label>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="primary"
            onClick={() => runRequest(false)}
            disabled={
              loading ||
              (operation === 'build_hazards' && !taskSummary.trim()) ||
              (operation === 'build_method_steps' && !taskSummary.trim()) ||
              (operation === 'improve_controls' && (form?.hazards?.length === 0 || (improveControlsHazardIndex != null && (improveControlsHazardIndex < 0 || improveControlsHazardIndex >= (form?.hazards?.length ?? 0)))))
            }
          >
            {loading ? (
              <>
                <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                Running...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-1.5" />
                Run
              </>
            )}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={handleEscalate} disabled={loading}>
            <ChevronUp className="h-4 w-4 mr-1.5" />
            Escalate to stronger model
          </Button>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  {consecutiveFailures >= 2
                    ? 'Server will use stronger model on retry. You can also use Escalate.'
                    : 'You can retry or escalate to the stronger model.'}
                </p>
                <Button type="button" variant="outline" size="sm" className="mt-2" onClick={handleRetry}>
                  Retry
                </Button>
              </div>
            </div>
          </div>
        )}

        {result && (
          <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
            {result.modelUsed && (
              <p className="text-xs text-gray-500 dark:text-gray-400">Model: {result.modelUsed}</p>
            )}
            {result.warnings?.length > 0 && (
              <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                {result.warnings.map((w, i) => (
                  <li key={i}>{typeof w === 'string' ? w : w.message ?? JSON.stringify(w)}</li>
                ))}
              </ul>
            )}

            {operation === 'build_hazards' && result.data?.hazards?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Hazards</h3>
                <div className="flex justify-end mb-2">
                  <Button type="button" variant="outline" size="sm" onClick={handleAcceptAllHazards}>
                    <Check className="h-4 w-4 mr-1" />
                    Accept all
                  </Button>
                </div>
                <ul className="space-y-3 max-h-60 overflow-y-auto">
                  {result.data.hazards.map((h, idx) => (
                    <li
                      key={idx}
                      className="p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-sm"
                    >
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {h.hazard_category ?? h.hazardCategory ?? 'Hazard'}: { (h.hazard_text ?? h.hazardText ?? '').slice(0, 80) }{ (h.hazard_text ?? h.hazardText ?? '').length > 80 ? '...' : '' }
                      </div>
                      <div className="flex justify-end gap-2 mt-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleAcceptHazard(idx)}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Accept
                        </Button>
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleRejectHazard(idx)}>
                          <X className="h-3 w-3 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
                {((result.data.ppe?.length) || (result.data.tools?.length) || (result.data.permits?.length) || result.data.emergency) && onAcceptPpeToolsPermits && (
                  <div className="mt-3">
                    <Button type="button" variant="outline" size="sm" onClick={handleAcceptPpeToolsPermitsBlock}>
                      <Check className="h-4 w-4 mr-1" />
                      Accept PPE / tools / permits / emergency block
                    </Button>
                  </div>
                )}
              </div>
            )}

            {operation === 'build_method_steps' && result.data?.method_steps?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Method steps</h3>
                <div className="flex justify-end mb-2">
                  <Button type="button" variant="outline" size="sm" onClick={handleAcceptAllMethodSteps}>
                    <Check className="h-4 w-4 mr-1" />
                    Accept all
                  </Button>
                </div>
                <ol className="space-y-2 list-decimal list-inside max-h-60 overflow-y-auto">
                  {result.data.method_steps.map((s, idx) => (
                    <li
                      key={idx}
                      className="p-2 rounded border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-sm"
                    >
                      <span className="font-medium">{s.step_title ?? s.stepTitle ?? `Step ${idx + 1}`}</span>
                      <div className="flex justify-end gap-2 mt-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleAcceptMethodStep(idx)}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Accept
                        </Button>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {operation === 'improve_controls' && result.data && (
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Improved controls</h3>
                <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-sm space-y-2">
                  {(result.data.improved_control_measures || []).map((c, i) => (
                    <div key={i}>
                      {c.control_strength && <span className="text-gray-500 dark:text-gray-400">{c.control_strength}: </span>}
                      {c.text}
                    </div>
                  ))}
                  {(result.data.residual_risk != null || result.data.notes) && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Residual risk: {result.data.residual_risk ?? '—'}. {result.data.notes ?? ''}
                    </p>
                  )}
                </div>
                {!acceptedBlocks.improve && onAcceptImproveControls && improveControlsHazardIndex != null && (
                  <Button type="button" variant="outline" size="sm" className="mt-2" onClick={handleAcceptImprove}>
                    <Check className="h-4 w-4 mr-1" />
                    Accept and apply to hazard
                  </Button>
                )}
              </div>
            )}

            {operation === 'quality_check' && result.data && (
              <div className="space-y-3">
                {result.data.summary && (
                  <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50">
                    <p className="text-sm font-medium">Overall rating: {result.data.summary.overall_rating ?? '—'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Missing critical: {result.data.summary.missing_critical_count ?? 0}, non-critical: {result.data.summary.missing_noncritical_count ?? 0}
                    </p>
                  </div>
                )}
                {(result.data.missing_critical?.length > 0) && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Missing critical</h4>
                    <ul className="text-sm list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {result.data.missing_critical.map((m, i) => (
                        <li key={i}>{m.description ?? m.code ?? JSON.stringify(m)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(result.data.missing_noncritical?.length > 0) && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Missing non-critical</h4>
                    <ul className="text-sm list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {result.data.missing_noncritical.map((m, i) => (
                        <li key={i}>{m.description ?? m.code ?? JSON.stringify(m)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {(result.data.weak_wording?.length > 0) && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Weak wording</h4>
                    <ul className="text-sm list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300">
                      {result.data.weak_wording.map((w, i) => (
                        <li key={i}>{w.location}: {w.suggestion ?? JSON.stringify(w)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </SlideOver>
  );
}
